import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { MapPinIcon } from '@heroicons/react/24/solid';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/components/Toast';
import { parseLocalDate } from '@/lib/localDate';
import type { SidebarGroup } from '@/features/groupRail/GroupRailListItem';
import type { SignupSlot, SignupEntry } from '@flock-types/signupSheets.types';
import { useSignupSheetDetail } from '@/hooks/useSignupSheets';
import { useGroupLeadership } from '@/hooks/useGroupLeadership';
import SlotRow from './SlotRow';
import SurveyOptionRow from './SurveyOptionRow';
import SheetMenu from './SheetMenu';
import AddSlotModal from './AddSlotModal';
import EditSlotModal from './EditSlotModal';
import AssignMemberModal from './AssignMemberModal';
import EditSheetModal from './EditSheetModal';

interface Props {
  group: SidebarGroup;
  sheetId: string;
}

/**
 * Sign-up sheet detail: slots with entries (or survey tallies), member
 * sign-up/withdraw/notes, and all leader management. Orchestrates the
 * modals; row rendering lives in SlotRow/EntryRow/SurveyOptionRow.
 */
export default function SheetDetailPane({ group, sheetId }: Props) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showToast } = useToast();
  const isLeader = useGroupLeadership(group.id);
  const detail = useSignupSheetDetail(sheetId, group.orgId);
  const {
    sheet,
    loading,
    error,
    refetch,
    signUp,
    vote,
    withdraw,
    deleteSheet,
    deleteSlot,
    deleteAllSlots,
    updateEntry,
    deleteEntry,
    updateSheet,
    isMutating,
  } = detail;
  const userId = user?.id;

  const [editMode, setEditMode] = useState(false);
  const [showAddSlot, setShowAddSlot] = useState(false);
  const [editingSlot, setEditingSlot] = useState<SignupSlot | null>(null);
  const [assignSlot, setAssignSlot] = useState<SignupSlot | null>(null);
  const [showEditSheet, setShowEditSheet] = useState(false);
  const [signingUpSlotId, setSigningUpSlotId] = useState<string | null>(null);

  // Split slots into upcoming and past using LOCAL dates (UTC ISO midnight
  // from a Postgres DATE renders as the previous day otherwise).
  const { activeSlots, pastSlots } = useMemo(() => {
    if (!sheet?.slots) return { activeSlots: [] as SignupSlot[], pastSlots: [] as SignupSlot[] };
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const active: SignupSlot[] = [];
    const past: SignupSlot[] = [];
    for (const slot of sheet.slots) {
      if (slot.slot_date && parseLocalDate(slot.slot_date) < today) past.push(slot);
      else active.push(slot);
    }
    return { activeSlots: active, pastSlots: past };
  }, [sheet?.slots]);

  const handleSignUp = async (slotId: string) => {
    setSigningUpSlotId(slotId);
    try {
      await signUp(slotId);
    } catch (e: any) {
      showToast(e?.response?.data?.message || 'This slot may already be full.', 'error');
    } finally {
      setSigningUpSlotId(null);
    }
  };

  const handleWithdraw = async (slotId: string) => {
    if (!window.confirm('Are you sure you want to withdraw from this slot?')) return;
    try {
      await withdraw(slotId);
    } catch {
      showToast('Failed to withdraw. Please try again.', 'error');
    }
  };

  const handleRemoveEntry = async (entry: SignupEntry) => {
    if (!window.confirm(`Remove ${entry.user_name}'s sign-up?`)) return;
    try {
      await deleteEntry(entry.id);
    } catch {
      showToast('Failed to remove sign-up.', 'error');
    }
  };

  const handleSaveNote = async (entry: SignupEntry, note: string | null) => {
    try {
      await updateEntry(entry.id, note);
    } catch {
      showToast('Failed to save note.', 'error');
      throw new Error('note-save-failed');
    }
  };

  const handleDeleteSlot = async (slotId: string) => {
    if (!window.confirm('Remove this slot? This will also remove any sign-ups for it.')) return;
    try {
      await deleteSlot(slotId);
      if (activeSlots.filter((s) => s.id !== slotId).length === 0) setEditMode(false);
    } catch {
      showToast('Failed to remove slot.', 'error');
    }
  };

  const handleDeleteAll = async () => {
    if (
      !window.confirm(
        `Delete all ${activeSlots.length} items and any sign-ups? This cannot be undone.`
      )
    )
      return;
    try {
      await deleteAllSlots();
      setEditMode(false);
    } catch {
      showToast('Failed to delete items.', 'error');
    }
  };

  const handlePin = async () => {
    try {
      await updateSheet({ is_pinned: !sheet?.is_pinned });
    } catch {
      showToast('Failed to update pin status.', 'error');
    }
  };

  const handleArchive = async () => {
    if (sheet?.status === 'archived') {
      try {
        await updateSheet({ status: 'active' });
        showToast('This sheet is now active again.', 'success');
        navigate(`/signups/${group.id}`);
      } catch {
        showToast('Failed to unarchive.', 'error');
      }
      return;
    }
    if (
      !window.confirm(
        'Archive this sheet? It will be hidden from the active list. You can view it later in archived sheets.'
      )
    )
      return;
    try {
      await updateSheet({ status: 'archived' });
      showToast('This sheet has been archived.', 'success');
      navigate(`/signups/${group.id}`);
    } catch {
      showToast('Failed to archive.', 'error');
    }
  };

  const handleDelete = async () => {
    if (
      !window.confirm(
        'Delete this sign-up sheet? This will permanently delete it and all sign-ups. This cannot be undone.'
      )
    )
      return;
    try {
      await deleteSheet();
      navigate(`/signups/${group.id}`);
    } catch {
      showToast('Failed to delete sheet.', 'error');
    }
  };

  const handleSurveyVote = async (slotId: string) => {
    try {
      await vote(slotId);
    } catch {
      showToast('Failed to record vote.', 'error');
    }
  };

  const backLink = (
    <button
      onClick={() => navigate(`/signups/${group.id}`)}
      className="flex items-center gap-1.5 text-sm font-medium text-flock-navy-500 transition hover:text-flock-navy-700"
    >
      <ArrowLeftIcon className="h-4 w-4" />
      Back to sign-up sheets
    </button>
  );

  if (loading) {
    return (
      <div className="flex h-full flex-col">
        <div className="border-b border-flock-gray-200 bg-white px-6 py-4">{backLink}</div>
        <div className="flex flex-1 items-center justify-center text-sm text-flock-navy-400">
          Loading sign-up sheet…
        </div>
      </div>
    );
  }

  if (error || !sheet) {
    return (
      <div className="flex h-full flex-col">
        <div className="border-b border-flock-gray-200 bg-white px-6 py-4">{backLink}</div>
        <div className="flex flex-1 flex-col items-center justify-center gap-3">
          <p className="text-sm text-flock-navy-500">{error || 'Sign-up sheet not found.'}</p>
          <button
            onClick={() => refetch()}
            className="rounded-lg bg-flock-navy-600 px-4 py-2 text-sm font-medium text-white hover:bg-flock-navy-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const isSurvey = sheet.sheet_type === 'survey';
  const addThing = sheet.sheet_type === 'date' ? 'Date' : isSurvey ? 'Option' : 'Item';

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between gap-3 border-b border-flock-gray-200 bg-white px-6 py-4">
        <div className="min-w-0">
          {backLink}
          <h1 className="mt-1 truncate text-lg font-semibold text-flock-navy-900">{sheet.title}</h1>
        </div>
        {isLeader && (
          <SheetMenu
            sheet={sheet}
            editMode={editMode}
            onPin={handlePin}
            onEditDetails={() => setShowEditSheet(true)}
            onToggleEditMode={() => setEditMode((v) => !v)}
            onArchive={handleArchive}
            onDelete={handleDelete}
          />
        )}
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        <div className="mx-auto max-w-3xl space-y-2">
          {/* Sheet info bar */}
          {(sheet.description || sheet.is_pinned) && (
            <div className="mb-4 rounded-xl border border-flock-gray-200 bg-white p-4">
              {sheet.description && (
                <p className="whitespace-pre-wrap text-sm text-flock-navy-600">
                  {sheet.description}
                </p>
              )}
              {sheet.is_pinned && (
                <span
                  className={`flex items-center gap-1 text-xs font-medium text-amber-600 ${sheet.description ? 'mt-2' : ''}`}
                >
                  <MapPinIcon className="h-3 w-3" />
                  Pinned
                </span>
              )}
            </div>
          )}

          {/* Delete All (edit mode) */}
          {isLeader && editMode && activeSlots.length > 0 && (
            <button
              onClick={handleDeleteAll}
              className="mb-2 w-full rounded-xl border-2 border-red-200 bg-red-50 py-3 text-sm font-medium text-red-500 transition hover:bg-red-100"
            >
              Delete All ({activeSlots.length})
            </button>
          )}

          {/* Active slots / survey options */}
          {activeSlots.length === 0 && pastSlots.length === 0 && (
            <p className="py-8 text-center text-sm text-flock-navy-400">
              {isLeader
                ? `No ${addThing.toLowerCase()}s yet — add the first one below.`
                : 'Nothing to sign up for yet. Check back soon!'}
            </p>
          )}

          {isSurvey
            ? activeSlots.map((slot) => (
                <SurveyOptionRow
                  key={slot.id}
                  slot={slot}
                  userId={userId}
                  disabled={isMutating}
                  onVote={() => handleSurveyVote(slot.id)}
                />
              ))
            : activeSlots.map((slot) => (
                <SlotRow
                  key={slot.id}
                  slot={slot}
                  userId={userId}
                  isLeader={isLeader}
                  isPast={false}
                  editMode={editMode}
                  busy={signingUpSlotId !== null}
                  signingUp={signingUpSlotId === slot.id}
                  onSignUp={() => handleSignUp(slot.id)}
                  onWithdraw={() => handleWithdraw(slot.id)}
                  onAssign={() => setAssignSlot(slot)}
                  onEdit={() => setEditingSlot(slot)}
                  onDeleteSlot={() => handleDeleteSlot(slot.id)}
                  onRemoveEntry={handleRemoveEntry}
                  onSaveNote={handleSaveNote}
                />
              ))}

          {/* Add slot button (leaders) */}
          {isLeader && !editMode && (
            <button
              onClick={() => setShowAddSlot(true)}
              className="mt-2 w-full rounded-xl border-2 border-dashed border-flock-gray-400 py-3 text-sm font-medium text-flock-navy-600 transition hover:border-flock-navy-500 hover:bg-flock-navy-50"
            >
              + Add {addThing}
            </button>
          )}

          {/* Past slots */}
          {pastSlots.length > 0 && (
            <div className="pt-4">
              <div className="mb-2 text-sm font-medium uppercase tracking-wide text-flock-navy-400">
                Past ({pastSlots.length})
              </div>
              <div className="space-y-2">
                {pastSlots.map((slot) => (
                  <SlotRow
                    key={slot.id}
                    slot={slot}
                    userId={userId}
                    isLeader={isLeader}
                    isPast
                    editMode={editMode}
                    busy={signingUpSlotId !== null}
                    signingUp={false}
                    onSignUp={() => {}}
                    onWithdraw={() => {}}
                    onAssign={() => {}}
                    onEdit={() => {}}
                    onDeleteSlot={() => handleDeleteSlot(slot.id)}
                    onRemoveEntry={handleRemoveEntry}
                    onSaveNote={handleSaveNote}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {showAddSlot && (
        <AddSlotModal
          sheet={sheet}
          addSlot={detail.addSlot}
          addSlotsBulk={detail.addSlotsBulk}
          isMutating={isMutating}
          onClose={() => setShowAddSlot(false)}
        />
      )}
      {editingSlot && (
        <EditSlotModal
          slot={editingSlot}
          updateSlot={detail.updateSlot}
          onDeleteSlot={() => handleDeleteSlot(editingSlot.id)}
          isMutating={isMutating}
          onClose={() => setEditingSlot(null)}
        />
      )}
      {assignSlot && (
        <AssignMemberModal
          groupId={group.id}
          slot={assignSlot}
          assignMember={detail.assignMember}
          onClose={() => setAssignSlot(null)}
        />
      )}
      {showEditSheet && (
        <EditSheetModal
          sheet={sheet}
          updateSheet={updateSheet}
          isMutating={isMutating}
          onClose={() => setShowEditSheet(false)}
        />
      )}
    </div>
  );
}
